import { FieldArray, useFormikContext } from "formik";
import SignificantParty from "./SignificantParty";
import ExitContinueButton from "../../helpers/ExitContinueButtons";
import { useEffect, useRef, useState } from "react";
import StageIndicator from "../../helpers/StageIndicator";
import { BusinessVerificationStage } from "../../verificationModels";
import instance, { ApiResponse } from "api";
import { SelectOption } from "@avamae/formbuilder/dist/FormSingleSelectField";
import { endpoints } from "config";
import useValidate from "../../helpers/touchAllFields";
import { DocumentRejection } from "../../Verification";
import { Toast } from "components/helpers/toast";
import useScrollToError from "../../helpers/useScrollToError";
import React from "react";
import { FormValues } from "../../verificationSchema";
import { ErrorM } from "components/helpers/ErrorM";
import Button from "components/ui/Button";

interface Props {
  values: any;
  submitError: boolean;
  submitErrorField: string;
  setSubmitError: React.SetStateAction<any>;
  rejectedDocuments: DocumentRejection | null | undefined;
}
interface DropdwonData {
  countries: { isoCode: string; countryName: string }[];
  states: { stateProvinceName: string; ansiCode: string }[];
}
export interface SignificantPartyValues {
  _id: number;
  number: number;
  firstName: string;
  lastName: string;
  title: string;
  dateOfBirth: string;
  nationality: string;
  email: string;
  phoneNumber: string;
  country: string;
  ssn: string;
  stateProvince: string;
  city: string;
  addressLine1: string;
  addressLine2: string;
  postcode: string;
  percentageSharesOwned: number | null;
  position: string;
  bControllingParty: boolean | null;
  proofOfAddressFilename: string;
}
export interface SignificantPartiesValues {
  significantParties: SignificantPartyValues[];
  lastId: number;
}
const significantPartyValues = {
  _id: 0,
  number: 0,
  firstName: "",
  lastName: "",
  title: "",
  dateOfBirth: "",
  nationality: "",
  email: "",
  phoneNumber: "",
  country: "",
  ssn: "",
  stateProvince: "",
  city: "",
  addressLine1: "",
  addressLine2: "",
  postcode: "",
  percentageSharesOwned: null,
  position: "",
  bControllingParty: null,
  proofOfAddressFilename: "",
};

const SignificantParties: React.FC<Props> = ({
  values,
  submitError,
  submitErrorField,
  setSubmitError,
  rejectedDocuments,
}) => {
  const nextIdRef = useRef<number>(1);
  const {
    isSubmitting,
    isValidating,
    setFieldTouched,
    errors,
  } = useFormikContext<FormValues>();
  const [availableCountries, setAvailableCountries] = useState<SelectOption[]>(
    []
  );
  const [availableStates, setAvailableStates] = useState<SelectOption[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const getDropdownData = async () => {
    try {
      const res = await instance.get<ApiResponse<DropdwonData>>(
        endpoints.verification.getDropdownData
      );

      if (res.data.status === "1") {
        setAvailableCountries(
          res.data.details.countries.map(country => ({
            value: country.isoCode,
            label: country.countryName,
          }))
        );
        setAvailableStates(
          res.data.details.states.map(state => ({
            value: state.ansiCode,
            label: state.stateProvinceName,
          }))
        );
      } else {
        setAvailableStates([]);
        setAvailableCountries([]);
      }
    } catch (err) {
      Toast.openGenericErrorToast();
      setAvailableStates([]);
      setAvailableCountries([]);
    }
  };

  useEffect(() => {
    getDropdownData();
  }, []);

  useValidate(setFieldTouched, {
    significantParties: values.significantParties,
  });

  let expandAll = useScrollToError(
    submitError,
    submitErrorField,
    isExpanded,
    setIsExpanded,
    setSubmitError
  );

  return (
    <>
      <StageIndicator
        currentStage={BusinessVerificationStage.SignificantParties}
      />
      <div className="SignificantParties">
        <h1>Information about significant parties</h1>
        <p>
          Please add details for all significant parties of the entity (either
          beneficial owners and/or control persons).
        </p>
        <ErrorM name="significantParties" />
        <FieldArray
          name="significantParties"
          render={arrayHelpers => (
            <div>
              {values.significantParties &&
              values.significantParties?.length > 0 ? (
                values.significantParties.map(
                  (significantParty: any, index: number) => (
                    <SignificantParty
                      key={index}
                      index={index}
                      availableCountries={availableCountries}
                      availableStates={availableStates}
                      arrayHelpers={arrayHelpers}
                      disableRemove={values.significantParties.length === 1}
                      expandAll={expandAll}
                      setIsExpanded={setIsExpanded}
                      rejectedDocuments={rejectedDocuments}
                      startExpanded={index === 0}
                    />
                  )
                )
              ) : (
                <div></div>
              )}
              <Button
                type="button"
                color="white"
                variety="full"
                onClick={() => {
                  arrayHelpers.push({
                    ...significantPartyValues,
                    _id: nextIdRef,
                  });
                  nextIdRef.current++;
                }}
              >
                Add Significant Party
              </Button>
            </div>
          )}
        />
        <ExitContinueButton disabled={isSubmitting || isValidating} />
      </div>
    </>
  );
};

export default SignificantParties;
